import type { ReactNode } from 'react';
import { Alert } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { DASHBOARD_RELEASE_PATH, SETTINGS_NAV, SIDEBAR_NAV, filterNav } from '@app/shared';
import type { NavItem } from '@app/shared';
import { useAuth } from '../features/auth/AuthProvider';
import { useReleaseState, useReleased } from './useRelease';

function firstOpenPath(items: NavItem[], open: (item: NavItem) => boolean): string | undefined {
  for (const item of items) {
    if (item.path && open(item)) return item.path;
    if (item.children) {
      const found = firstOpenPath(item.children, open);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Wraps the page area. A page whose menu entry is not released shows a notice instead; the home page
 * (the dashboard) moves to the first released screen the user may open.
 */
export function ReleaseGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { can } = useAuth();
  const { isPending } = useReleaseState();
  const released = useReleased();

  // A moment while the switches load, so a closed page never starts its own requests.
  if (isPending) return null;
  if (released(pathname)) return <>{children}</>;

  if (pathname === DASHBOARD_RELEASE_PATH) {
    const open = (item: NavItem) => released(item.path);
    const next =
      firstOpenPath(filterNav(SIDEBAR_NAV, can), open) ??
      firstOpenPath(filterNav(SETTINGS_NAV, can), open);
    if (next) return <Navigate to={next} replace />;
  }

  return (
    <Alert severity="info" sx={{ maxWidth: 640 }}>
      This module is not released yet. It will open in a later release.
    </Alert>
  );
}
